"use client";

import { cn } from "@punchup/utils/cn";
import { MessageSquare } from "lucide-react";
import { ReactNode, useRef } from "react";

import { useSentryFeedback } from "../bug-reports/useSentryFeedback";
import { Button } from "../../shadcn/ui/button";

const FEEDBACK_FORM_TITLE = "Report a Bug or Send Feedback";

const DEFAULT_LABEL = <span className="hidden md:inline">Give feedback</span>;

interface FeedbackButtonProps {
  className?: string;
  /** `"button"` (default) is the original shadcn ghost button used by the
   *  dashboard mount sites. `"link"` renders a bare underlined text button —
   *  the newsletter beta banner's design contract. */
  variant?: "button" | "link";
  /** Passed through to `useSentryFeedback` as the feedback modal's title.
   *  Defaults to the original title both existing mount sites rely on. */
  formTitle?: string;
  /** Defaults to the existing "Give feedback" span treatment. */
  label?: ReactNode;
  /** Fires alongside Sentry's own `attachTo` click listener. */
  onClick?: () => void;
}

export function FeedbackButton({
  className,
  variant = "button",
  formTitle = FEEDBACK_FORM_TITLE,
  label = DEFAULT_LABEL,
  onClick,
}: FeedbackButtonProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const isAvailable = useSentryFeedback(buttonRef, { formTitle });

  if (!isAvailable) return null;

  if (variant === "link") {
    return (
      <button
        ref={buttonRef}
        type="button"
        onClick={onClick}
        className={cn(
          "shrink-0 text-sm font-medium text-primary underline underline-offset-2 hover:opacity-80",
          className
        )}
      >
        {label}
      </button>
    );
  }

  return (
    <Button
      ref={buttonRef}
      variant="ghost"
      onClick={onClick}
      className={className}
    >
      <MessageSquare className="h-4 w-4 md:mr-2" />
      {label}
    </Button>
  );
}
